import React, { useState, Fragment, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calculator } from '../components/Calculator';
import { WaydevProof } from '../components/home/WaydevProof';
import { BarChart3, ShieldCheck, Zap, ArrowRight, ChevronDown, GitPullRequest, Activity, Lock, Server, Cpu, AlertTriangle, Terminal } from 'lucide-react';
import { Link } from 'react-router-dom';
export function Home() {
  const [activeTab, setActiveTab] = useState(0);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const howItWorksTabs = [{
    title: 'CONNECT_TELEMETRY',
    icon: Server,
    content: 'Integrate TokenMeter with Git providers (GitHub, GitLab, Bitbucket) and AI tool APIs via secure webhooks. Ingest commit data, PR cycles, and API logs without touching source code.',
    metric: 'SETUP < 5m'
  }, {
    title: 'MAP_ACTIVE_USAGE',
    icon: Activity,
    content: 'Cross-reference provisioned seats against actual developer activity. Track accepted completions, chat queries, and context window usage to build a precise map of active users.',
    metric: 'VISIBILITY: 100%'
  }, {
    title: 'AUTOMATE_RECLAMATION',
    icon: GitPullRequest,
    content: "Set custom thresholds (e.g., 'Downgrade if < 10 completions in 14d'). TokenMeter automatically alerts managers or de-provisions idle seats to eliminate shadow IT.",
    metric: 'SAVINGS: 30%+'
  }];
  const faqs = [{
    q: 'How much does GitHub Copilot or Cursor really cost for an enterprise?',
    a: "While base prices hover around $39-$40 per user/month, the true cost includes 'idle seat waste'. Our data shows up to 30% of provisioned enterprise seats go unused by developers who spend more time in design docs or meetings than writing code. TokenMeter helps you track and reclaim this wasted spend."
  }, {
    q: 'Is consumption billing (Claude Code, OpenAI Codex) cheaper than per-seat?',
    a: "It depends on your team's workflow. Consumption models eliminate idle seat waste, meaning you only pay when code is generated. However, heavy refactoring sessions with massive 200k+ token context windows can cause sudden billing spikes. TokenMeter tracks API token usage to prevent bill shock."
  }, {
    q: 'How do I track AI coding tool ROI?',
    a: "Tracking ROI requires connecting AI tool usage data with engineering metrics. TokenMeter correlates accepted AI completions with PR cycle times, deployment frequency, and code churn to show you exactly how much productivity lift you're getting for your spend."
  }, {
    q: 'Does TokenMeter read my source code?',
    a: 'No. TokenMeter only requires metadata (commit hashes, timestamps, PR statuses) and billing API access. We never read, store, or analyze your proprietary source code, ensuring strict SOC2 compliance and enterprise-grade security.'
  }];
  return <div className="bg-slate-950 min-h-screen font-sans selection:bg-emerald-500/30" data-id="element-276">
      {/* Live Ticker Tape */}
      <div className="border-b border-slate-800 bg-slate-900/50 flex overflow-hidden font-mono text-[10px] py-1.5 uppercase tracking-widest text-emerald-500/80 whitespace-nowrap" data-id="element-277">
        <div className="animate-ticker flex gap-8" data-id="element-278">
          {[...Array(3)].map((_, i) => <Fragment key={i} data-id="element-279">
              <span data-id="element-280">SYS_STATUS: ONLINE</span>
              <span data-id="element-281">COPILOT: $39/MO</span>
              <span data-id="element-282">CURSOR: $40/MO</span>
              <span data-id="element-283">CLAUDE: API</span>
              <span data-id="element-284">WINDSURF: $40/MO</span>
              <span data-id="element-285">AUGMENT: $50/MO</span>
              <span data-id="element-286">GEMINI: $45/MO</span>
              <span data-id="element-287">CODEX: API</span>
              <span data-id="element-288">WASTE_DETECTED: TRUE</span>
            </Fragment>)}
        </div>
      </div>

      {/* Main Console Grid */}
      <main className="max-w-[1600px] mx-auto border-x border-slate-800 bg-slate-950 flex flex-col" data-id="element-289">
        {/* Top Console: Hero + Calculator */}
        <div className="grid lg:grid-cols-12 border-b border-slate-800" data-id="element-290">
          {/* Hero Text */}
          <div className="lg:col-span-5 p-8 md:p-12 lg:p-16 border-b lg:border-b-0 lg:border-r border-slate-800 flex flex-col justify-center relative overflow-hidden" data-id="element-291">
            <div className="absolute inset-0 bg-dot-grid opacity-20 pointer-events-none" data-id="element-292"></div>
            <div className="relative z-10" data-id="element-293">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-900 border border-slate-800 text-slate-400 text-[10px] font-mono font-bold uppercase tracking-widest mb-8" data-id="element-294">
                <span className="w-1.5 h-1.5 bg-emerald-500 animate-pulse glow-box-emerald" data-id="element-295"></span>
                POWERED_BY_WAYDEV
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-slate-50 leading-tight mb-6 tracking-tighter" data-id="element-296">
                Stop guessing your <br data-id="element-297" />
                <span className="text-emerald-500 glow-text-emerald" data-id="element-298">
                  AI tool spend.
                </span>
              </h1>
              <p className="text-lg text-slate-400 leading-relaxed font-mono text-sm mb-10 max-w-md" data-id="element-299">
                Engineering leaders use TokenMeter to track, allocate, and
                optimize spend across Copilot, Cursor, and Claude. Calculate
                exposure.
              </p>
              <a href="https://waydev.co/pricing" className="inline-flex items-center gap-2 bg-slate-50 text-slate-950 px-6 py-3 font-mono font-bold hover:bg-emerald-400 transition-colors text-sm uppercase tracking-widest" data-id="element-300">
                [ INITIALIZE_TRACKING ] <ArrowRight size={16} data-id="element-301" />
              </a>
            </div>
          </div>

          {/* Calculator Module */}
          <div className="lg:col-span-7 bg-slate-900/10" data-id="element-302">
            <Calculator data-id="element-303" />
          </div>
        </div>

        {/* Middle Console: Sprawl Audit + Capabilities */}
        <div className="grid lg:grid-cols-12 border-b border-slate-800" data-id="element-304">
          {/* Capabilities List */}
          <div className="lg:col-span-4 border-b lg:border-b-0 lg:border-r border-slate-800 flex flex-col" data-id="element-305">
            <div className="p-6 border-b border-slate-800 bg-slate-900/50" data-id="element-306">
              <h3 className="text-[10px] font-mono text-slate-500 uppercase tracking-widest flex items-center gap-2" data-id="element-307">
                <Terminal size={12} data-id="element-308" /> SYSTEM_CAPABILITIES
              </h3>
            </div>
            <div className="flex-1 flex flex-col" data-id="element-309">
              {[{
              icon: BarChart3,
              title: 'TRACK_UTILIZATION',
              desc: 'Stop paying for idle seats. Reallocate licenses automatically.'
            }, {
              icon: Zap,
              title: 'COMPARE_ROI',
              desc: 'Tie AI tool usage directly to PR cycle times and deployment frequency.'
            }, {
              icon: ShieldCheck,
              title: 'ENTERPRISE_GOV',
              desc: 'Set budget alerts and track API token consumption.'
            }].map((prop, i) => <div key={i} className={`p-8 flex-1 flex flex-col justify-center group hover:bg-slate-900/30 transition-colors ${i < 2 ? 'border-b border-slate-800' : ''}`} data-id="element-310">
                  <prop.icon size={24} className="text-emerald-500 mb-4 group-hover:glow-text-emerald" data-id="element-311" />
                  <h4 className="text-sm font-mono font-bold text-slate-50 mb-2" data-id="element-312">
                    {prop.title}
                  </h4>
                  <p className="text-sm text-slate-400 font-mono leading-relaxed" data-id="element-313">
                    {prop.desc}
                  </p>
                </div>)}
            </div>
          </div>

          {/* Sprawl Audit Visual */}
          <div className="lg:col-span-8 p-8 md:p-12 lg:p-16 flex flex-col justify-center relative" data-id="element-314">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-red-500/10 border border-red-500/20 text-red-400 text-[10px] font-mono font-bold uppercase tracking-widest mb-8 self-start" data-id="element-315">
              <AlertTriangle size={12} data-id="element-316" /> WARN: THE_SPRAWL_PROBLEM
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-50 mb-6 tracking-tight" data-id="element-317">
              You are overpaying for AI assistants.
            </h2>
            <div className="grid md:grid-cols-2 gap-12" data-id="element-318">
              <div className="space-y-4 text-sm font-mono text-slate-400 leading-relaxed" data-id="element-319">
                <p data-id="element-320">
                  Companies purchase <strong data-id="element-321">Copilot Enterprise</strong> or{' '}
                  <strong data-id="element-322">Cursor Business</strong> licenses for every engineer
                  by default.
                </p>
                <p data-id="element-323">
                  The result is <strong data-id="element-324">idle seat waste</strong>—paying $40/mo
                  for developers who haven't accepted a code completion in
                  weeks.
                </p>
                <p data-id="element-325">
                  Meanwhile, consumption-based tools like{' '}
                  <strong data-id="element-326">Claude Code</strong> risk sudden billing spikes from
                  unoptimized context windows.
                </p>
              </div>

              <div className="bg-slate-950 border border-slate-800 p-6 relative overflow-hidden" data-id="element-327">
                <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-transparent pointer-events-none" data-id="element-328"></div>
                <h3 className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-6 flex justify-between" data-id="element-329">
                  <span data-id="element-330">&gt; LICENSE_AUDIT</span>
                  <span className="text-emerald-500 animate-pulse" data-id="element-331">LIVE</span>
                </h3>

                <div className="space-y-6 relative z-10" data-id="element-332">
                  <div data-id="element-333">
                    <div className="flex justify-between text-xs font-mono mb-2" data-id="element-334">
                      <span className="text-slate-400" data-id="element-335">PROVISIONED_SEATS</span>
                      <span className="text-slate-300" data-id="element-336">500</span>
                    </div>
                    <div className="h-1 bg-slate-900 overflow-hidden" data-id="element-337">
                      <motion.div initial={{
                      width: 0
                    }} whileInView={{
                      width: '100%'
                    }} viewport={{
                      once: true
                    }} transition={{
                      duration: 1
                    }} className="h-full bg-slate-600" data-id="element-338" />
                    </div>
                  </div>

                  <div data-id="element-339">
                    <div className="flex justify-between text-xs font-mono mb-2" data-id="element-340">
                      <span className="text-emerald-400" data-id="element-341">ACTIVE_USERS_7D</span>
                      <span className="text-emerald-400" data-id="element-342">342</span>
                    </div>
                    <div className="h-1 bg-slate-900 overflow-hidden" data-id="element-343">
                      <motion.div initial={{
                      width: 0
                    }} whileInView={{
                      width: '68%'
                    }} viewport={{
                      once: true
                    }} transition={{
                      duration: 1,
                      delay: 0.2
                    }} className="h-full bg-emerald-500 glow-box-emerald" data-id="element-344" />
                    </div>
                  </div>

                  <div className="pt-6 border-t border-slate-800 border-dashed mt-6" data-id="element-345">
                    <div className="text-[10px] font-mono text-red-500 uppercase tracking-widest mb-1" data-id="element-346">
                      IDENTIFIED_WASTE
                    </div>
                    <div className="text-3xl font-mono font-bold text-red-400" style={{
                    textShadow: '0 0 15px rgba(248, 113, 113, 0.3)'
                  }} data-id="element-347">
                      $6,320<span className="text-sm text-red-400/50" data-id="element-348">/MO</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pipeline / How It Works */}
        <div className="grid lg:grid-cols-12 border-b border-slate-800" data-id="element-349">
          <div className="lg:col-span-4 p-8 border-b lg:border-b-0 lg:border-r border-slate-800 bg-slate-900/20 flex flex-col justify-center" data-id="element-350">
            <h2 className="text-2xl font-bold text-slate-50 mb-4 tracking-tight" data-id="element-351">
              Telemetry Pipeline
            </h2>
            <p className="text-sm font-mono text-slate-400 leading-relaxed" data-id="element-352">
              A seamless, secure pipeline from your Git provider to your finance
              dashboard.
            </p>
          </div>
          <div className="lg:col-span-8 grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-800" data-id="element-353">
            {howItWorksTabs.map((tab, idx) => <div key={idx} className="p-8 hover:bg-slate-900/50 transition-colors group" data-id="element-354">
                <div className="w-10 h-10 bg-slate-950 border border-slate-800 flex items-center justify-center text-emerald-500 mb-6 group-hover:glow-box-emerald transition-all" data-id="element-355">
                  <tab.icon size={18} data-id="element-356" />
                </div>
                <h3 className="text-xs font-mono font-bold text-slate-50 mb-4" data-id="element-357">
                  {tab.title}
                </h3>
                <p className="text-xs font-mono text-slate-400 leading-relaxed mb-6" data-id="element-358">
                  {tab.content}
                </p>
                <div className="text-[10px] font-mono text-emerald-500" data-id="element-359">
                  &gt; {tab.metric}
                </div>
              </div>)}
          </div>
        </div>

        {/* Waydev Proof Section */}
        <WaydevProof data-id="element-360" />

        {/* Data Streams (Guides) */}
        <div className="border-b border-slate-800" data-id="element-361">
          <div className="p-6 border-b border-slate-800 bg-slate-900/50 flex flex-col sm:flex-row justify-between items-center gap-4" data-id="element-362">
            <h3 className="text-[10px] font-mono text-slate-500 uppercase tracking-widest flex items-center gap-2" data-id="element-363">
              <Terminal size={12} data-id="element-364" /> DATA_STREAMS :: COST_GUIDES
            </h3>
            <Link to="/compare" className="text-[10px] font-mono text-emerald-500 hover:text-emerald-400 uppercase tracking-widest flex items-center gap-1" data-id="element-365">
              [ VIEW_FULL_COMPARISON ] <ArrowRight size={12} data-id="element-366" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:border-b-0 border-slate-800" data-id="element-367">
            {[{
            name: 'GitHub Copilot',
            path: '/guides/copilot',
            price: '$39/mo',
            type: 'Enterprise Seat'
          }, {
            name: 'Cursor',
            path: '/guides/cursor',
            price: '$40/mo',
            type: 'Business Seat'
          }, {
            name: 'Claude Code',
            path: '/guides/claude-code',
            price: 'API',
            type: 'Consumption'
          }, {
            name: 'Windsurf',
            path: '/guides/windsurf',
            price: '$40/mo',
            type: 'Team Seat'
          }, {
            name: 'Augment Code',
            path: '/guides/augment',
            price: '$50/mo',
            type: 'Seat + Credits'
          }, {
            name: 'Gemini Code Assist',
            path: '/guides/gemini',
            price: '$45/mo',
            type: 'Enterprise Seat'
          }, {
            name: 'OpenAI Codex',
            path: '/guides/codex',
            price: 'API',
            type: 'Consumption'
          }, {
            name: 'Compare All',
            path: '/compare',
            price: 'DATA',
            type: 'Full Matrix',
            isLink: true
          }].map((tool, i) => <Link key={tool.name} to={tool.path} className={`p-6 border-slate-800 hover:bg-slate-900/50 transition-colors group relative ${i % 4 !== 3 ? 'lg:border-r' : ''} ${i % 2 === 0 ? 'sm:border-r' : ''} ${i < 4 ? 'lg:border-b' : ''} ${i < 6 ? 'sm:border-b' : ''}`} data-id="element-368">
                <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-4" data-id="element-369">
                  {tool.type}
                </div>
                <h3 className={`text-lg font-bold mb-2 ${tool.isLink ? 'text-emerald-400' : 'text-slate-50'}`} data-id="element-370">
                  {tool.name}
                </h3>
                <div className="font-mono text-sm text-slate-400 group-hover:text-emerald-400 transition-colors" data-id="element-371">
                  {tool.price}
                </div>
                <ArrowRight size={16} className="absolute bottom-6 right-6 text-slate-700 group-hover:text-emerald-500 transition-colors" data-id="element-372" />
              </Link>)}
          </div>
        </div>

        {/* Query Log (FAQ) & Integrations */}
        <div className="grid lg:grid-cols-12" data-id="element-373">
          <div className="lg:col-span-4 border-b lg:border-b-0 lg:border-r border-slate-800 flex flex-col" data-id="element-374">
            <div className="p-6 border-b border-slate-800 bg-slate-900/50" data-id="element-375">
              <h3 className="text-[10px] font-mono text-slate-500 uppercase tracking-widest flex items-center gap-2" data-id="element-376">
                <Terminal size={12} data-id="element-377" /> INTEGRATIONS
              </h3>
            </div>
            <div className="p-8 flex-1 flex flex-col justify-center" data-id="element-378">
              <div className="flex flex-col gap-6 opacity-50 grayscale hover:grayscale-0 transition-all duration-500" data-id="element-379">
                <div className="flex items-center gap-3 text-lg font-mono font-bold text-slate-300" data-id="element-380">
                  <GitPullRequest size={20} data-id="element-381" /> GITHUB
                </div>
                <div className="flex items-center gap-3 text-lg font-mono font-bold text-slate-300" data-id="element-382">
                  <Cpu size={20} data-id="element-383" /> GITLAB
                </div>
                <div className="flex items-center gap-3 text-lg font-mono font-bold text-slate-300" data-id="element-384">
                  <Server size={20} data-id="element-385" /> BITBUCKET
                </div>
                <div className="flex items-center gap-3 text-lg font-mono font-bold text-slate-300" data-id="element-386">
                  <Lock size={20} data-id="element-387" /> AZURE_DEVOPS
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8 flex flex-col" data-id="element-388">
            <div className="p-6 border-b border-slate-800 bg-slate-900/50" data-id="element-389">
              <h3 className="text-[10px] font-mono text-slate-500 uppercase tracking-widest flex items-center gap-2" data-id="element-390">
                <Terminal size={12} data-id="element-391" /> QUERY_LOG :: FAQ
              </h3>
            </div>
            <div className="divide-y divide-slate-800" data-id="element-392">
              {faqs.map((faq, idx) => <div key={idx} className="bg-slate-950" data-id="element-393">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full text-left px-8 py-6 flex items-center justify-between hover:bg-slate-900/30 transition-colors focus:outline-none" data-id="element-394">
                    <span className="font-mono text-sm text-slate-300 pr-8" data-id="element-395">
                      &gt; {faq.q}
                    </span>
                    <ChevronDown size={16} className={`text-emerald-500 transition-transform duration-300 shrink-0 ${openFaq === idx ? 'rotate-180' : ''}`} data-id="element-396" />
                  </button>
                  <AnimatePresence data-id="element-397">
                    {openFaq === idx && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.2
                }} data-id="element-398">
                        <div className="px-8 pb-6 text-slate-400 font-mono text-xs leading-relaxed border-t border-slate-800 border-dashed pt-4" data-id="element-399">
                          {faq.a}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>)}
            </div>
          </div>
        </div>
      </main>
    </div>;
}